import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { getAuthToken } from "@/lib/queryClient";

type Summary = { totalCalls: number; answered: number; missed: number; avgWaitSec: number; avgTalkSec: number };
type AgentRow = { agentId: string; name?: string; calls: number; avgTalkSec: number };
type QueueRow = { queueId: string; name: string; calls: number; avgWaitSec: number };
type DispoRow = { disposition: string; count: number };

export default function CallReports() {
  const summary = useQuery<Summary>({ queryKey: ["/api/call-reports/summary"] });
  const agents = useQuery<AgentRow[]>({ queryKey: ["/api/call-reports/agents"] });
  const queues = useQuery<QueueRow[]>({ queryKey: ["/api/call-reports/queues"] });
  const dispo = useQuery<DispoRow[]>({ queryKey: ["/api/call-reports/dispositions"] });

  async function exportCsv() {
    const t = getAuthToken();
    const r = await fetch("/api/call-reports/export", { headers: t ? { Authorization: `Bearer ${t}` } : {} });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "call-report.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="page-call-reports">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Call Reports</h1>
          <p className="text-sm text-muted-foreground">Performance and usage analytics.</p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="button-export-csv"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <KPI label="Total" value={summary.data?.totalCalls ?? 0} />
        <KPI label="Answered" value={summary.data?.answered ?? 0} />
        <KPI label="Missed" value={summary.data?.missed ?? 0} />
        <KPI label="Avg wait" value={`${summary.data?.avgWaitSec ?? 0}s`} />
        <KPI label="Avg talk" value={`${summary.data?.avgTalkSec ?? 0}s`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle className="text-sm">By Agent</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b"><th className="py-2">Agent</th><th>Calls</th><th>Avg talk</th></tr></thead>
              <tbody>{(agents.data || []).map(a => (
                <tr key={a.agentId} className="border-b" data-testid={`row-agent-report-${a.agentId}`}><td className="py-2">{a.name || a.agentId.slice(0,8)}</td><td>{a.calls}</td><td>{a.avgTalkSec}s</td></tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-sm">By Queue</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b"><th className="py-2">Queue</th><th>Calls</th><th>Avg wait</th></tr></thead>
              <tbody>{(queues.data || []).map(q => (
                <tr key={q.queueId} className="border-b" data-testid={`row-queue-report-${q.queueId}`}><td className="py-2">{q.name}</td><td>{q.calls}</td><td>{q.avgWaitSec}s</td></tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2"><CardHeader><CardTitle className="text-sm">Dispositions</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {(dispo.data || []).map(d => (
              <Badge key={d.disposition} variant="outline" data-testid={`badge-dispo-${d.disposition}`}>{d.disposition}: {d.count}</Badge>
            ))}
            {(dispo.data || []).length === 0 && <span className="text-xs text-muted-foreground">No data</span>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
function KPI({ label, value }: { label: string; value: number | string }) {
  return <Card><CardContent className="p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-semibold" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,"-")}`}>{value}</div></CardContent></Card>;
}
